import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery, useMutation, gql } from '@apollo/client';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import classNames from 'classnames';
import LoadingSpinner from '../loading-spinner';
import { useNotifications } from '../../notifications';
import { Slideout } from '../slideout';
import styles from './NotificationPreferencesSlideout.module.scss';

// GraphQL queries and mutations
const GET_TEAM_NOTIFICATION_PREFERENCES = gql`
  query GetTeamNotificationPreferences($teamId: ID!) {
    teamNotificationPreferences(teamId: $teamId) {
      id
      allLeadTimes
    }
  }
`;

const UPDATE_TEAM_NOTIFICATION_PREFERENCES = gql`
  mutation UpdateTeamNotificationPreferences($teamId: ID!, $eventLeadTimes: JSON!) {
    updateTeamNotificationPreferences(teamId: $teamId, eventLeadTimes: $eventLeadTimes) {
      teamNotificationPreference {
        id
        allLeadTimes
      }
      errors
    }
  }
`;

interface NotificationPreferencesSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
}

// Event type labels
const EVENT_LABELS: Record<string, string> = {
  // Lease events
  'lease_execution': 'Lease Execution',
  'lease_commencement': 'Lease Commencement',
  'rent_commencement': 'Rent Commencement',
  'lease_expiration': 'Lease Expiration',
  'renewal_notice_deadline': 'Renewal Notice Deadline',
  'termination_option_deadline': 'Termination Option Deadline',
  'expansion_option_deadline': 'Expansion Option Deadline',
  'contraction_option_deadline': 'Contraction Option Deadline',
  'rofr_rofo_deadline': 'ROFR/ROFO Deadline',
  'rent_escalation_first': 'First Rent Escalation',
  'cam_reconciliation_due': 'CAM Reconciliation Due',
  'security_deposit_return': 'Security Deposit Return',
  'estoppel_due': 'Estoppel Due',
  'snda_timing': 'SNDA Timing',
  'insurance_renewal': 'Insurance Renewal',
  'landlord_delivery': 'Landlord Delivery',
  'tenant_work_deadline': 'Tenant Work Deadline',
  'retail_opening_deadline': 'Retail Opening Deadline',
  'cotenancy_cure_deadline': 'Co-tenancy Cure Deadline',
  // Sale events (PSA)
  'psa_execution': 'Sale Agreement Execution',
  'earnest_money_due': 'Earnest Money Due',
  'due_diligence_end': 'Due Diligence End',
  'title_objection_due': 'Title Objection Due',
  'seller_cure_deadline': 'Seller Cure Deadline',
  'financing_contingency': 'Financing Contingency',
  'appraisal_contingency': 'Appraisal Contingency',
  'estoppel_delivery_psa': 'Estoppel Delivery',
  'closing_date': 'Closing Date',
  'outside_closing_date': 'Outside Closing Date',
  'exchange_45_day': '1031 Exchange (45 Day)',
  'exchange_180_day': '1031 Exchange (180 Day)',
};

const LEASE_EVENTS = [
  'lease_execution',
  'lease_commencement',
  'rent_commencement',
  'lease_expiration',
  'renewal_notice_deadline',
  'termination_option_deadline',
  'expansion_option_deadline',
  'contraction_option_deadline',
  'rofr_rofo_deadline',
  'rent_escalation_first',
  'cam_reconciliation_due',
  'security_deposit_return',
  'estoppel_due',
  'snda_timing',
  'insurance_renewal',
  'landlord_delivery',
  'tenant_work_deadline',
  'retail_opening_deadline',
  'cotenancy_cure_deadline',
];

const PSA_EVENTS = [
  'psa_execution',
  'earnest_money_due',
  'due_diligence_end',
  'title_objection_due',
  'seller_cure_deadline',
  'financing_contingency',
  'appraisal_contingency',
  'estoppel_delivery_psa',
  'closing_date',
  'outside_closing_date',
  'exchange_45_day',
  'exchange_180_day',
];

const NotificationPreferencesSlideout: React.FC<NotificationPreferencesSlideoutProps> = ({
  isOpen,
  onClose,
  teamId,
}) => {
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [leadTimes, setLeadTimes] = useState<Record<string, number>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<'lease' | 'sale'>('lease');

  // Fetch current preferences
  const { loading, data, refetch } = useQuery(GET_TEAM_NOTIFICATION_PREFERENCES, {
    variables: { teamId },
    skip: !isOpen || !teamId,
  });

  // Update mutation
  const [updatePreferences, { loading: saving }] = useMutation(UPDATE_TEAM_NOTIFICATION_PREFERENCES, {
    onCompleted: (result) => {
      if (result.updateTeamNotificationPreferences.errors.length === 0) {
        notifySuccess({ message: 'Notification preferences updated successfully' });
        setHasChanges(false);
        refetch();
      } else {
        notifyError({ message: result.updateTeamNotificationPreferences.errors[0] });
      }
    },
    onError: (error) => {
      notifyError({ message: error.message });
    },
  });

  // Load preferences when data arrives
  useEffect(() => {
    if (data?.teamNotificationPreferences?.allLeadTimes) {
      setLeadTimes(data.teamNotificationPreferences.allLeadTimes);
      setHasChanges(false);
    }
  }, [data]);

  const handleLeadTimeChange = (eventType: string, value: string) => {
    const days = parseInt(value, 10);
    if (!isNaN(days) && days >= 0 && days <= 365) {
      setLeadTimes(prev => ({
        ...prev,
        [eventType]: days,
      }));
      setHasChanges(true);
    }
  };

  const handleSave = () => {
    updatePreferences({
      variables: {
        teamId,
        eventLeadTimes: leadTimes,
      },
    });
  };

  const handleCancel = () => {
    // Reset to original values
    if (data?.teamNotificationPreferences?.allLeadTimes) {
      setLeadTimes(data.teamNotificationPreferences.allLeadTimes);
    }
    setHasChanges(false);
    onClose();
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={handleCancel}
      title={<FormattedMessage id="tracker.preferences.title" defaultMessage="Notification Preferences" />}
      width="medium"
    >
      <div className={styles.preferencesWrapper}>
        <div className={styles.content}>
        {/* Tabs */}
        <Row className={classNames(styles.tabsContainer, 'm-0 p-0')}>
          <Col
            xs={6}
            className={classNames(
              styles.tab,
              activeTab === 'lease' && styles['tab--active']
            )}
            onClick={() => setActiveTab('lease')}
          >
            <FormattedMessage id="tracker.preferences.lease.tab" defaultMessage="Lease Events" />
          </Col>
          <Col
            xs={6}
            className={classNames(
              styles.tab,
              activeTab === 'sale' && styles['tab--active']
            )}
            onClick={() => setActiveTab('sale')}
          >
            <FormattedMessage id="tracker.preferences.sale.tab" defaultMessage="Sale Events" />
          </Col>
        </Row>

        {loading ? (
          <div className={styles.loadingContainer}>
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Tab Content */}
            <div className={styles.tabContent}>
              {activeTab === 'lease' ? (
                // Lease Events
                <div className={styles.eventGroup}>
                  {LEASE_EVENTS.map(eventType => (
                    <div key={eventType} className={styles.eventRow}>
                      <label className={styles.eventLabel}>
                        {EVENT_LABELS[eventType]}
                      </label>
                      <div className={styles.inputGroup}>
                        <Form.Control
                          type="number"
                          min="0"
                          max="365"
                          value={leadTimes[eventType] || 0}
                          onChange={(e) => handleLeadTimeChange(eventType, e.target.value)}
                          className={styles.daysInput}
                        />
                        <span className={styles.daysLabel}>
                          {leadTimes[eventType] === 0 ? (
                            <FormattedMessage id="tracker.preferences.sameDay" defaultMessage="same day" />
                          ) : leadTimes[eventType] === 1 ? (
                            <FormattedMessage id="tracker.preferences.dayBefore" defaultMessage="day before" />
                          ) : (
                            <FormattedMessage id="tracker.preferences.daysBefore" defaultMessage="days before" />
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Sale Events
                <div className={styles.eventGroup}>
                  {PSA_EVENTS.map(eventType => (
                    <div key={eventType} className={styles.eventRow}>
                      <label className={styles.eventLabel}>
                        {EVENT_LABELS[eventType]}
                      </label>
                      <div className={styles.inputGroup}>
                        <Form.Control
                          type="number"
                          min="0"
                          max="365"
                          value={leadTimes[eventType] || 0}
                          onChange={(e) => handleLeadTimeChange(eventType, e.target.value)}
                          className={styles.daysInput}
                        />
                        <span className={styles.daysLabel}>
                          {leadTimes[eventType] === 0 ? (
                            <FormattedMessage id="tracker.preferences.sameDay" defaultMessage="same day" />
                          ) : leadTimes[eventType] === 1 ? (
                            <FormattedMessage id="tracker.preferences.dayBefore" defaultMessage="day before" />
                          ) : (
                            <FormattedMessage id="tracker.preferences.daysBefore" defaultMessage="days before" />
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <Button
            variant="outline-primary"
            onClick={handleCancel}
            disabled={saving}
          >
            <FormattedMessage id="tracker.preferences.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            variant={!hasChanges && !saving ? "outline-secondary" : "primary"}
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <FormattedMessage id="tracker.preferences.saving" defaultMessage="Saving..." />
            ) : (
              <FormattedMessage id="tracker.preferences.save" defaultMessage="Save" />
            )}
          </Button>
        </div>
        </div>
      </div>
    </Slideout>
  );
};

export default NotificationPreferencesSlideout;